import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Menu,
    LogOut,
    User as UserIcon,
    LayoutDashboard,
    ShieldCheck,
    MessageCircleQuestion,
    ChevronDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

const PUBLIC_LINKS = [
    { label: "Guide Rapide", children: [
        { label: "Ripartizione spese Proprietario e Inquilino", to: "/faq/ripartizione-spese" },
        { label: "Tabella delle Maggioranze Assembleari", to: "/faq/maggioranze-assembleari" },
        { label: "Come richiedere un'assemblea straordinaria", to: "/faq/assemblea-straordinaria" },
    ]},
    { label: "Codice Civile", to: "/codice-civile" },
    { label: "Servizi NowGestioni", to: "/risorse" },
    { label: "Chi siamo", to: "/chi-siamo" },
];

function initialsOf(name) {
    if (!name) return "U";
    return name
        .split(/\s+/)
        .filter(Boolean)
        .map((s) => s[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
}

function BrandMark() {
    return (
        <Link
            to="/"
            data-testid="header-logo"
            className="inline-flex items-center gap-2 sm:gap-3 group shrink-0"
            aria-label="CondominioNow"
        >
            <img
                src="/brand-header.png"
                alt=""
                className="h-[52px] w-auto object-contain shrink-0 group-hover:opacity-90 transition"
            />
            <span
                data-testid="header-brand-text"
                className="font-serif text-xl sm:text-2xl lg:text-[24px] font-semibold text-slate-900 tracking-tight whitespace-nowrap"
            >
                Condominio<span className="text-accent">Now</span>
            </span>
        </Link>
    );
}

export default function Header() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    const authed = user && typeof user === "object";
    const isAdmin = authed && user.role === "admin";
    const initials = authed ? initialsOf(user.full_name) : "U";
    const askHref = authed
        ? isAdmin
            ? "/admin"
            : "/dashboard"
        : "/registrati";

    const handleLogout = async () => {
        await logout();
        setOpen(false);
        navigate("/");
    };

    return (
        <header className="sticky top-0 z-40 w-full border-b border-slate-200/70 bg-white/90 backdrop-blur-lg shadow-[0_1px_0_rgba(15,23,42,0.03)]">
            <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-3 sm:gap-4 px-4 sm:px-6 lg:px-8">
                <BrandMark />

                {/* Desktop nav */}
                <nav className="hidden lg:flex items-center gap-0.5 shrink-0">
                    {PUBLIC_LINKS.map((item) => (
                        item.children ? (
                            <DropdownMenu key={item.label}>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="nav-guide-rapide"
                                        className="relative inline-flex items-center gap-1 px-3 py-2 text-[14.5px] font-medium text-slate-700 hover:text-primary transition whitespace-nowrap outline-none after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-[2px] after:h-[2px] after:bg-primary after:rounded-full after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-200"
                                    >
                                        {item.label}
                                        <ChevronDown className="size-4 opacity-70" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start" className="w-80">
                                    {item.children.map((child) => (
                                        <DropdownMenuItem
                                            key={child.to}
                                            data-testid={`nav-guide-${child.to.split("/").pop()}`}
                                            onClick={() => navigate(child.to)}
                                            className="cursor-pointer whitespace-normal leading-snug py-2"
                                        >
                                            {child.label}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        ) : (
                            <Link
                                key={item.label}
                                to={item.to}
                                data-testid={`nav-${item.to.slice(1)}`}
                                className="relative px-3 py-2 text-[14.5px] font-medium text-slate-700 hover:text-primary transition whitespace-nowrap after:content-[''] after:absolute after:left-3 after:right-3 after:-bottom-[2px] after:h-[2px] after:bg-primary after:rounded-full after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-200"
                            >
                                {item.label}
                            </Link>
                        )
                    ))}
                </nav>

                {/* Desktop actions */}
                <div className="hidden lg:flex items-center gap-2">
                    {authed ? (
                        <>
                            <Button
                                data-testid="header-faq-btn"
                                onClick={() => navigate("/faq")}
                                className="bg-primary text-primary-foreground hover:bg-[#2570B0] font-semibold rounded-full px-5"
                            >
                                Domande e Risposte Veloci
                            </Button>
                            <Button
                                data-testid="header-ask-btn"
                                onClick={() => navigate(askHref)}
                                className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full px-5"
                            >
                                Invia una domanda
                            </Button>
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="header-user-menu"
                                        className="ml-1 flex items-center gap-2 rounded-full pl-3 pr-1 py-1 hover:bg-slate-100 transition"
                                    >
                                        <span className="text-sm font-medium max-w-[140px] truncate">
                                            {user.full_name}
                                        </span>
                                        <Avatar className="size-8">
                                            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                                                {initials}
                                            </AvatarFallback>
                                        </Avatar>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel className="font-normal">
                                        <div className="text-sm font-semibold">
                                            {user.full_name}
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate">
                                            {user.email}
                                        </div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    {isAdmin ? (
                                        <DropdownMenuItem
                                            data-testid="menu-admin"
                                            onClick={() => navigate("/admin")}
                                        >
                                            <ShieldCheck className="size-4 mr-2" /> Pannello admin
                                        </DropdownMenuItem>
                                    ) : (
                                        <DropdownMenuItem
                                            data-testid="menu-dashboard"
                                            onClick={() => navigate("/dashboard")}
                                        >
                                            <LayoutDashboard className="size-4 mr-2" /> Le tue richieste
                                        </DropdownMenuItem>
                                    )}
                                    <DropdownMenuItem
                                        data-testid="menu-profile"
                                        onClick={() => navigate("/profilo")}
                                    >
                                        <UserIcon className="size-4 mr-2" /> Profilo
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        data-testid="menu-logout"
                                        onClick={handleLogout}
                                        className="text-destructive focus:text-destructive"
                                    >
                                        <LogOut className="size-4 mr-2" /> Esci
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    ) : (
                        <>
                            <Button
                                data-testid="header-login-btn"
                                variant="ghost"
                                onClick={() => navigate("/login")}
                                className="text-slate-700 hover:bg-slate-100"
                            >
                                Accedi
                            </Button>
                            <Button
                                data-testid="header-faq-btn"
                                onClick={() => navigate("/faq")}
                                className="bg-primary text-primary-foreground hover:bg-[#2570B0] font-semibold rounded-full px-5 shadow-sm"
                            >
                                Domande e Risposte Veloci
                            </Button>
                            <Button
                                data-testid="header-ask-btn"
                                onClick={() => navigate("/registrati")}
                                className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full px-5 shadow-sm"
                            >
                                Invia una domanda
                            </Button>
                        </>
                    )}
                </div>

                {/* Mobile */}
                <Sheet open={open} onOpenChange={setOpen}>
                    <SheetTrigger asChild className="lg:hidden">
                        <Button
                            data-testid="header-mobile-menu"
                            variant="ghost"
                            size="icon"
                            aria-label="Apri menu"
                        >
                            <Menu className="size-6" />
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="right" className="w-80 flex flex-col p-0">
                        <div className="flex flex-1 min-h-0 flex-col gap-6 pt-8 pb-6 px-6 overflow-y-auto">
                            {authed && (
                                <div className="pb-4 border-b">
                                    <div className="flex items-center gap-3">
                                        <Avatar className="size-10">
                                            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                                                {initials}
                                            </AvatarFallback>
                                        </Avatar>
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold truncate">
                                                {user.full_name}
                                            </div>
                                            <div className="text-xs text-muted-foreground truncate">
                                                {user.email}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <nav className="flex flex-col gap-1">
                                {PUBLIC_LINKS.map((item) => (
                                    item.children ? (
                                        <div key={item.label} className="mt-1">
                                            <div className="px-3 pt-3 pb-1 text-[11px] uppercase tracking-widest text-primary font-semibold">
                                                {item.label}
                                            </div>
                                            {item.children.map((child) => (
                                                <Link
                                                    key={child.to}
                                                    to={child.to}
                                                    onClick={() => setOpen(false)}
                                                    data-testid={`nav-mobile-guide-${child.to.split("/").pop()}`}
                                                    className="block px-3 py-2 text-[15px] font-medium text-slate-800 hover:text-primary transition leading-snug"
                                                >
                                                    {child.label}
                                                </Link>
                                            ))}
                                        </div>
                                    ) : (
                                        <Link
                                            key={item.label}
                                            to={item.to}
                                            onClick={() => setOpen(false)}
                                            className="px-3 py-3 text-base font-medium text-slate-800 hover:text-primary transition"
                                        >
                                            {item.label}
                                        </Link>
                                    )
                                ))}
                            </nav>
                            <div className="mt-auto pt-6 border-t flex flex-col gap-2">
                                <Button
                                    className="w-full bg-primary text-primary-foreground hover:bg-[#2570B0] font-semibold rounded-full"
                                    onClick={() => {
                                        setOpen(false);
                                        navigate("/faq");
                                    }}
                                >
                                    Domande e Risposte Veloci
                                </Button>
                                <Button
                                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90 font-semibold rounded-full"
                                    onClick={() => {
                                        setOpen(false);
                                        navigate(askHref);
                                    }}
                                >
                                    Invia una domanda
                                </Button>
                                {authed ? (
                                    <>
                                        {isAdmin ? (
                                            <Button
                                                variant="outline"
                                                className="w-full rounded-full"
                                                onClick={() => {
                                                    setOpen(false);
                                                    navigate("/admin");
                                                }}
                                            >
                                                Pannello admin
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="outline"
                                                className="w-full rounded-full"
                                                onClick={() => {
                                                    setOpen(false);
                                                    navigate("/dashboard");
                                                }}
                                            >
                                                Le tue richieste
                                            </Button>
                                        )}
                                        <Button
                                            variant="ghost"
                                            className="w-full rounded-full"
                                            onClick={() => {
                                                setOpen(false);
                                                navigate("/profilo");
                                            }}
                                        >
                                            Profilo
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            className="w-full rounded-full text-destructive hover:text-destructive"
                                            onClick={handleLogout}
                                        >
                                            <LogOut className="size-4 mr-2" /> Esci
                                        </Button>
                                    </>
                                ) : (
                                    <Button
                                        variant="outline"
                                        className="w-full rounded-full"
                                        onClick={() => {
                                            setOpen(false);
                                            navigate("/login");
                                        }}
                                    >
                                        Accedi
                                    </Button>
                                )}
                            </div>
                        </div>
                    </SheetContent>
                </Sheet>
            </div>
        </header>
    );
}
